import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { Location } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ReportsService } from '../../services/reports.service';
import { InventoryService } from '../../services/inventory.service';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { UsersService } from '../../services/users.service';
import { PrintService } from '../../services/print.service';

@Component({
	selector: 'app-non-turning-inventory',
	templateUrl: './non-turning-inventory.component.html',
	styleUrls: ['./non-turning-inventory.component.scss']
})
export class NonTurningInventoryComponent implements OnInit {
    @ViewChild(MatSort, { static: false }) sort: MatSort;
    @ViewChild(MatPaginator) paginator: MatPaginator;
    @ViewChild('print_table') printtable: ElementRef;

    filterForm: UntypedFormGroup;
    branches: any[] = [];
    brands: any[] = [];
    categories: any[] = [];

    loading = false;
    tableData = new MatTableDataSource<any>([]);
    displayedColumns: string[] = [];
    rowsData: any[] = [];
    filteredRows: any[] = [];

    totals = { total_value: '0.00', total_quantity: 0 };
    color: any = 'blue';
    sending = false;
    filename = 'Non_Turning_Inventory';
    slowCoverDays = 0;

    constructor(
        private formBuilder: UntypedFormBuilder,
        private reportsService: ReportsService,
        private inventoryService: InventoryService,
        private globalSearchService: GlobalSearchService,
        private usersService: UsersService,
        private printService: PrintService,
        private location: Location
    ) {
    	this.color = this.globalSearchService.getColor();
    	const today = new Date();
    	const prior = new Date();
    	prior.setDate(today.getDate() - 365);

    	this.filterForm = this.formBuilder.group({
    		fromdate: prior,
    		todate: today,
    		days_without_sales: 90,
    		min_stock_quantity: 1,
    		include_zero_stock: false,
    		locations: [[]],
    		categories: [[]],
    		brands: [[]]
    	});
    }

    ngOnInit(): void {
    	this.loadFilters();
    }

    loadFilters() {
    	this.reportsService.getBranches().subscribe((results: any) => {
    		this.branches = results || [];
    		const user = this.usersService.getLocalUser();
    		if (user && user.user && user.user.defaultlocation && user.user.defaultlocation.loccode) {
    			this.filterForm.patchValue({ locations: [user.user.defaultlocation.loccode] });
    		}
    	});

    	this.inventoryService.getBrands().subscribe((results: any) => {
    		this.brands = results || [];
    	});

    	this.inventoryService.getItemCategories().subscribe((results: any) => {
    		this.categories = results || [];
    	});
    }

    runReport() {
    	this.loading = true;
    	const payload = this.buildPayload();
    	this.reportsService.getNonTurningInventory(payload).subscribe((resp: any) => {
    		const rows = resp && resp.data ? resp.data : [];
    		this.tableData = new MatTableDataSource<any>(rows);
    		this.rowsData = rows;
    		this.updateVisibleRows();
    		const preferredOrder = [
    			'locationname',
    			'loccode',
    			'categorydescription',
    			'stockid',
    			'description',
    			'quantity_on_hand',
    			'unit_cost',
    			'inventory_value',
    			'total_sales_amount_lookback',
    			'total_units_sold_lookback',
    			'avg_daily_sales',
    			'inventory_cover_days',
    			'last_sale_date',
    			'days_since_last_sale'
    		];
    		if (rows && rows.length) {
    			const keys = Object.keys(rows[0]);
    			const rest = keys.filter(k => !preferredOrder.includes(k) && k !== 'categoryid');
    			// Always show preferred columns (even if a specific row omits a field)
    			this.displayedColumns = preferredOrder.concat(rest);
    		} else {
    			// Show headers even when empty to make exports consistent
    			this.displayedColumns = preferredOrder;
    		}
    		if (this.sort) { this.tableData.sort = this.sort; }
    		if (this.paginator) { this.tableData.paginator = this.paginator; }
    		this.totals = resp && resp.totals ? resp.totals : { total_value: '0.00', total_quantity: 0 };
    		this.loading = false;
    	}, _ => {
    		this.tableData = new MatTableDataSource<any>([]);
    		this.rowsData = [];
    		this.filteredRows = [];
    		this.displayedColumns = [];
    		this.totals = { total_value: '0.00', total_quantity: 0 };
    		this.loading = false;
    	});
    }

    prettyHeader(col: string): string {
    	const map: Record<string, string> = {
    		locationname: 'Location',
    		categorydescription: 'Category',
    		stockid: 'Stock ID',
    		quantity_on_hand: 'Quantity On Hand',
    		unit_cost: 'Unit Cost',
    		inventory_value: 'Inventory Value',
    		total_sales_amount_lookback: 'Sales Amount ($)',
    		total_units_sold_lookback: 'Sales Units',
    		last_sale_date: 'Last Sale Date',
    		days_since_last_sale: 'Days Since Last Sale'
    	};
    	if (map[col]) {return map[col];}
    	return col.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    }

    formatCell(row: any, col: string): any {
    	const value = row[col];
    	if (value === null || value === undefined) { return ''; }
    	switch (col) {
    	case 'inventory_value':
    	case 'unit_cost':
    	case 'total_sales_amount_lookback':
    		return Number(value).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
    	case 'last_sale_date': {
    		if (!value) { return ''; }
    		const d = new Date(String(value));
    		if (isNaN(d.getTime())) { return String(value); }
    		try {
    			return new Intl.DateTimeFormat(navigator.language || 'en-US', {
    				year: 'numeric', month: 'short', day: '2-digit'
    			}).format(d);
    		} catch (_) {
    			return d.toLocaleDateString();
    		}
    	}
    	case 'quantity_on_hand':
    	case 'days_since_last_sale': {
    		const num = Number(value);
    		if (!isFinite(num) || num >= 9999) { return '-'; }
    		return num.toLocaleString('en-US');
    	}
    	case 'total_units_sold_lookback':
    	case 'avg_daily_sales':
    	case 'inventory_cover_days':
    		return Number(value).toLocaleString('en-US');
    	default:
    		return value;
    	}
    }

    isNumeric(col: string): boolean {
    	return [
    		'quantity_on_hand',
    		'unit_cost',
    		'inventory_value',
    		'days_since_last_sale',
    		'total_units_sold_lookback',
    		'avg_daily_sales',
    		'inventory_cover_days'
    	].includes(col);
    }

    private buildPayload() {
    	const v = this.filterForm.value;
    	const fmt = (d: any) => {
    		if (!d) { return ''; }
    		const date = (d instanceof Date) ? d : new Date(d);
    		const yyyy = date.getFullYear();
    		const mm = String(date.getMonth() + 1).padStart(2, '0');
    		const dd = String(date.getDate()).padStart(2, '0');
    		return `${yyyy}-${mm}-${dd}`;
    	};
    	return {
    		fromdate: fmt(v.fromdate),
    		todate: fmt(v.todate),
    		days_without_sales: Number(v.days_without_sales) || 0,
    		min_stock_quantity: Number(v.min_stock_quantity) || 0,
    		include_zero_stock: !!v.include_zero_stock,
    		locations: Array.isArray(v.locations) ? v.locations : [],
    		categories: Array.isArray(v.categories) ? v.categories : [],
    		brands: Array.isArray(v.brands) ? v.brands : []
    	};
    }

    updateVisibleRows(): void {
    	if (!this.rowsData) { this.filteredRows = []; return; }
    	const minCover = Number(this.slowCoverDays) || 0;
    	if (minCover > 0) {
    		this.filteredRows = this.rowsData.filter(r => Number(r.inventory_cover_days || 0) >= minCover);
    	} else {
    		this.filteredRows = this.rowsData.slice();
    	}
    }

    onCoverDaysChange(val: any) {
    	const n = Number(val);
    	this.slowCoverDays = isNaN(n) ? 0 : n;
    	this.updateVisibleRows();
    }

    trackRow = (_: number, r: any) => `${r.loccode}|${r.stockid}`;

    // Using reactive form Date values directly; no ngModel bindings

    back(): void {
    	this.location.back();
    }

    exportPdf() {
    	this.sending = true;
    	const now = new Date();
    	const encoded: string = this.globalSearchService.base64encode(this.printtable.nativeElement.innerHTML);
    	const data = {
    		content: encoded,
    		filename: `${this.filename}_${now.getFullYear()}-${now.getMonth()+1}-${now.getDate()}`,
    		title: `Non-Turning Inventory ${this.filterForm.value.fromdate} - ${this.filterForm.value.todate}`,
    		landscape: true
    	};
    	this.printService.pdf(data).subscribe((result: any) => {
    		this.sending = false;
    		this.globalSearchService.downloadPdf(result.content, data.filename);
    	}, () => { this.sending = false; });
    }

    exportXls() {
    	this.sending = true;
    	const now = new Date();
    	const encoded: string = this.globalSearchService.base64encode(this.printtable.nativeElement.innerHTML);
    	const data = {
    		content: encoded,
    		filename: `${this.filename}_${now.getFullYear()}-${now.getMonth()+1}-${now.getDate()}`,
    		title: `Non-Turning Inventory ${this.filterForm.value.fromdate} - ${this.filterForm.value.todate}`,
    		subtitle: `Qty: ${this.totals.total_quantity} | Value: ${this.totals.total_value}`
    	};
    	this.printService.xls(data).subscribe((result: any) => {
    		this.sending = false;
    		this.globalSearchService.downloadXls(result.content, data.filename);
    	}, () => { this.sending = false; });
    }

}


